import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { useT } from "../../i18n/index.js";
import type { TranslationKey } from "../../i18n/index.js";

interface JoinResult {
  sessionId: string;
  playerId: string;
  playerToken: string;
  nickname: string;
}

interface NicknameEntryProps {
  pin: string;
  quizTitle: string;
  twoStepRequired?: boolean;
  onJoined: (result: JoinResult) => void;
}

const COLOR_LABELS = ["red", "blue", "yellow", "green"] as const;
const COLOR_KEYS: Record<(typeof COLOR_LABELS)[number], TranslationKey> = {
  red: "common.red",
  blue: "common.blue",
  yellow: "common.yellow",
  green: "common.green",
};

function colorLabel(t: (key: TranslationKey) => string, color: string): string {
  if (color === "red") return t(COLOR_KEYS.red);
  if (color === "blue") return t(COLOR_KEYS.blue);
  if (color === "yellow") return t(COLOR_KEYS.yellow);
  if (color === "green") return t(COLOR_KEYS.green);
  return color;
}

export function NicknameEntry({
  pin,
  quizTitle,
  twoStepRequired = false,
  onJoined,
}: NicknameEntryProps) {
  const t = useT();
  const [nickname, setNickname] = useState("");
  const [requiresTwoStep, setRequiresTwoStep] = useState(twoStepRequired);
  const [twoStepPattern, setTwoStepPattern] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatingName, setGeneratingName] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ twoStepRequired?: boolean }>(`/join/${pin}`)
      .then((data) => {
        if (!cancelled) setRequiresTwoStep(data.twoStepRequired ?? twoStepRequired);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pin, twoStepRequired]);

  async function generateNickname() {
    setGeneratingName(true);
    try {
      const data = await api.get<{ nickname: string }>("/join/nickname");
      setNickname(data.nickname);
      setError("");
    } catch {
      setError(t("join.suggestFailed"));
    } finally {
      setGeneratingName(false);
    }
  }

  function appendTwoStepColor(color: string) {
    setTwoStepPattern((prev) =>
      prev.length >= 3 ? [color] : [...prev, color],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = nickname.trim();
    if (!trimmed) {
      setError(t("join.enterName"));
      return;
    }
    if (trimmed.length > 24) {
      setError(t("join.nameTooLong"));
      return;
    }
    if (requiresTwoStep && twoStepPattern.length !== 3) {
      setError(t("join.patternRequired"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await api.post<JoinResult>(`/join/${pin}`, {
        nickname: trimmed,
        ...(requiresTwoStep ? { twoStepPattern } : {}),
      });
      onJoined(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("join.nameTaken"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(err.message);
      } else if (err instanceof ApiError && err.status === 422) {
        setError(t("join.notAccepting"));
      } else {
        setError(t("error.generic"));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="join-form" onSubmit={handleSubmit} aria-label={t("join.chooseName")}>
      {quizTitle && (
        <p className="join-quiz-name">{quizTitle}</p>
      )}
      <label className="join-field" htmlFor="nickname-input">
        <span>{t("join.yourName")}</span>
        <div className="join-input-row">
          <input
            id="nickname-input"
            className="join-text-input"
            type="text"
            maxLength={24}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder={t("join.yourNameHere")}
            autoFocus
            aria-label={t("join.yourName")}
            aria-describedby={error ? "nickname-error" : "nickname-hint"}
          />
          <button
            type="button"
            className="join-secondary-button"
            onClick={() => void generateNickname()}
            disabled={generatingName}
          >
            {generatingName ? t("join.suggestingName") : t("join.suggestName")}
          </button>
        </div>
      </label>
      <p id="nickname-hint" className="join-hint">
        {t("join.maximumName")}
      </p>
      {requiresTwoStep && (
        <div className="join-two-step">
          <p className="join-hint">{t("join.enterPattern")}</p>
          <div className="join-pattern-preview" aria-label={t("join.selectedPattern")}>
            {twoStepPattern.map((color, index) => (
              <span
                key={`${color}-${index}`}
                className="join-pattern-dot"
                data-color={color}
                aria-label={colorLabel(t, color)}
              />
            ))}
          </div>
          <div className="join-color-grid" role="group" aria-label={t("join.colorChoices")}>
            {COLOR_LABELS.map((color) => (
              <button
                key={color}
                type="button"
                className="join-color-button"
                data-color={color}
                onClick={() => appendTwoStepColor(color)}
                aria-label={t(COLOR_KEYS[color])}
              />
            ))}
          </div>
          <button
            type="button"
            className="join-secondary-button"
            onClick={() => setTwoStepPattern([])}
          >
            {t("join.clearPattern")}
          </button>
        </div>
      )}
      {error && (
        <p id="nickname-error" role="alert" className="join-error">
          {error}
        </p>
      )}
      <button className="join-primary-button" type="submit" disabled={loading}>
        {loading ? t("join.joining") : t("landing.joinTitle")}
      </button>
    </form>
  );
}
